import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useUnsavedChangesSource } from '../appUpdate/unsavedChanges';
import type { RelatedPickItem, StoreRelatedPicksView } from '../../types/relatedProducts';

export type PickListKey = 'related' | 'upsell';

export interface StoreRelatedPicksState {
  productId: string;
  loading: boolean;
  error: boolean;
  canEdit: boolean;
  draft: Record<PickListKey, RelatedPickItem[]>;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  add: (list: PickListKey, item: RelatedPickItem) => void;
  remove: (list: PickListKey, id: string) => void;
  move: (list: PickListKey, index: number, delta: -1 | 1) => void;
  save: () => Promise<unknown>;
  discard: () => void;
}

const idsOf = (items: RelatedPickItem[]) => items.map((i) => i.id).join('|');
const EMPTY: Record<PickListKey, RelatedPickItem[]> = { related: [], upsell: [] };

/**
 * The store's related and upsell picks for one product (flag
 * `retailer_related_products`, MOT-705), edited locally and saved in one PUT.
 * Called by the product details dialog, not by the section, so unsaved picks
 * stay guarded (`useUnsavedChangesSource`) while another tab is open.
 */
export function useStoreRelatedPicks(productId: string | undefined, enabled: boolean): StoreRelatedPicksState | null {
  const queryClient = useQueryClient();
  const queryKey = ['store-related-picks', productId];
  const query = useQuery({
    queryKey,
    queryFn: () => apiService.getStoreRelatedPicks(productId!),
    enabled: enabled && !!productId,
    staleTime: 30_000,
  });
  const saved = useMemo(
    () => (query.data ? { related: query.data.related, upsell: query.data.upsell } : EMPTY),
    [query.data],
  );
  const [draft, setDraft] = useState<Record<PickListKey, RelatedPickItem[]>>(saved);
  // A fresh read (another product, or after a save) resets the draft.
  useEffect(() => setDraft(saved), [saved]);

  const mutation = useMutation({
    mutationFn: (next: Record<PickListKey, RelatedPickItem[]>) =>
      apiService.saveStoreRelatedPicks(productId!, {
        related: next.related.map((i) => i.id),
        upsell: next.upsell.map((i) => i.id),
      }),
    onSuccess: (view: StoreRelatedPicksView) => queryClient.setQueryData(queryKey, view),
  });

  const dirty = enabled && !!query.data && (idsOf(draft.related) !== idsOf(saved.related) || idsOf(draft.upsell) !== idsOf(saved.upsell));
  const save = () => mutation.mutateAsync(draft);
  const discard = () => setDraft(saved);
  useUnsavedChangesSource({ dirty, save, discard });

  if (!enabled || !productId) return null;
  return {
    productId,
    loading: query.isLoading,
    error: query.isError,
    canEdit: !!query.data?.canEdit,
    draft,
    dirty,
    saving: mutation.isPending,
    saveError: mutation.error ? (mutation.error as Error).message : null,
    add: (list, item) =>
      setDraft((prev) => {
        if (item.id === productId || prev.related.some((i) => i.id === item.id) || prev.upsell.some((i) => i.id === item.id)) return prev;
        return { ...prev, [list]: [...prev[list], item] };
      }),
    remove: (list, id) => setDraft((prev) => ({ ...prev, [list]: prev[list].filter((i) => i.id !== id) })),
    move: (list, index, delta) =>
      setDraft((prev) => {
        const items = [...prev[list]];
        const target = index + delta;
        if (target < 0 || target >= items.length) return prev;
        [items[index], items[target]] = [items[target], items[index]];
        return { ...prev, [list]: items };
      }),
    save,
    discard,
  };
}
